import { MapPin, Phone, Mail } from "lucide-react";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const Contact = () => {
  return (
    <section
      id="contact"
      className="py-20 md:py-28 bg-white"
      aria-labelledby="contact-title"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-left mb-12 md:mb-16">
          <h2
            id="contact-title"
            className="text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight font-['Manrope']"
          >
            Prenez contact
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl">
            Discutons de vos besoins. Premier entretien sans engagement.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* WhatsApp CTA */}
          <div className="order-2 lg:order-1 flex flex-col items-center justify-center">
            <div className="w-full max-w-md text-center space-y-8">
              <div className="w-20 h-20 mx-auto rounded-full bg-green-50 flex items-center justify-center">
                <WhatsAppIcon className="w-10 h-10 text-green-600" />
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 font-['Manrope'] mb-3">
                  Contactez-nous directement
                </h3>
                <p className="text-gray-600">
                  Envoyez-nous un message sur WhatsApp pour une réponse rapide et personnalisée.
                </p>
              </div>

              <a
                href="https://wa.link/atadcl"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-green-600 hover:bg-green-700 text-white rounded-full px-10 py-4 text-lg font-medium transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
                data-testid="whatsapp-contact-btn"
              >
                <WhatsAppIcon className="w-6 h-6" />
                Nous contacter sur WhatsApp
              </a>
            </div>
          </div>

          {/* Contact Info */}
          <div className="order-1 lg:order-2">
            <div className="bg-gray-50 rounded-xl p-8 h-fit">
              <h3 className="text-xl font-semibold text-gray-900 font-['Manrope'] mb-6">
                Nos coordonnées
              </h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-50 flex-shrink-0">
                    <MapPin className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Adresse</p>
                    <p className="text-gray-600 mt-1">
                      Chemin de la Musardière 5<br />
                      1131 Tolochenaz<br />
                      Suisse
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-50 flex-shrink-0">
                    <Phone className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Téléphone</p>
                    <a
                      href="tel:+41799616033"
                      className="text-gray-600 hover:text-blue-600 transition-colors mt-1 block"
                      data-testid="contact-phone-link"
                    >
                      +41 79 961 60 33
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-blue-50 flex-shrink-0">
                    <Mail className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Email</p>
                    <a
                      href="mailto:compta@performancetransmission.ch"
                      className="text-gray-600 hover:text-blue-600 transition-colors mt-1 block"
                      data-testid="contact-email-link"
                    >
                      compta@performancetransmission.ch
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
